"use client"

import type React from "react"
import { createContext, useContext, useEffect, useState } from "react"
import type { User } from "@/lib/types"

interface AuthContextType {
  user: User | null
  login: (email: string, password: string) => Promise<boolean>
  register: (userData: RegisterData) => Promise<boolean>
  logout: () => void
  updateProfile: (updates: Partial<User>) => void
  isLoading: boolean
}

interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
  username: string
  language: "en" | "ar"
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Mock users data
const mockUsers: User[] = [
  {
    id: "1",
    email: "sarah@example.com",
    username: "sarah_m",
    firstName: "Sarah",
    lastName: "Mohammed",
    avatar: "/woman-profile.png",
    bio: "Displaced from Syria, now helping others navigate resettlement in Germany.",
    location: "Berlin, Germany",
    language: "en",
    role: "user",
    isVerified: true,
    joinedAt: new Date("2023-01-15"),
    lastActive: new Date(),
    followersCount: 234,
    followingCount: 89,
    postsCount: 45,
  },
  {
    id: "2",
    email: "admin@example.com",
    username: "admin",
    firstName: "Admin",
    lastName: "User",
    avatar: "/admin-profile.png",
    bio: "Platform administrator",
    location: "Global",
    language: "en",
    role: "admin",
    isVerified: true,
    joinedAt: new Date("2022-01-01"),
    lastActive: new Date(),
    followersCount: 1000,
    followingCount: 50,
    postsCount: 120,
  },
]

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check for stored user session
    const storedUser = localStorage.getItem("dwp_user")
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser))
      } catch (error) {
        localStorage.removeItem("dwp_user")
      }
    }
    setIsLoading(false)
  }, [])

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true)

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000))

    const foundUser = mockUsers.find((u) => u.email === email)
    if (foundUser && password === "password") {
      const updatedUser = { ...foundUser, lastActive: new Date() }
      setUser(updatedUser)
      localStorage.setItem("dwp_user", JSON.stringify(updatedUser))
      setIsLoading(false)
      return true
    }

    setIsLoading(false)
    return false
  }

  const register = async (userData: RegisterData): Promise<boolean> => {
    setIsLoading(true)

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000))

    // Check if user already exists
    const existingUser = mockUsers.find((u) => u.email === userData.email || u.username === userData.username)
    if (existingUser) {
      setIsLoading(false)
      return false
    }

    const newUser: User = {
      id: Date.now().toString(),
      email: userData.email,
      username: userData.username,
      firstName: userData.firstName,
      lastName: userData.lastName,
      avatar: `/placeholder.svg?height=40&width=40&query=${userData.firstName}+profile`,
      bio: "",
      location: "",
      language: userData.language,
      role: "user",
      isVerified: false,
      joinedAt: new Date(),
      lastActive: new Date(),
      followersCount: 0,
      followingCount: 0,
      postsCount: 0,
    }

    mockUsers.push(newUser)
    setUser(newUser)
    localStorage.setItem("dwp_user", JSON.stringify(newUser))
    setIsLoading(false)
    return true
  }

  const logout = () => {
    setUser(null)
    localStorage.removeItem("dwp_user")
  }

  const updateProfile = (updates: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...updates }
      setUser(updatedUser)
      localStorage.setItem("dwp_user", JSON.stringify(updatedUser))

      // Update in mock data
      const userIndex = mockUsers.findIndex((u) => u.id === user.id)
      if (userIndex !== -1) {
        mockUsers[userIndex] = updatedUser
      }
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        register,
        logout,
        updateProfile,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
